import axios from "axios";
import { setToken } from "../utils/tokens";

// Create a new instance of axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL + "/api/v1",
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
    Accept: "application/json",
  },
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("tykoon_access");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${JSON.parse(accessToken)}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => response, // Pass successful responses directly
  async (error) => {
    // Handle 403 - Permission Denied
    if (error.response && error.response.status === 403) {
      // Check if already on permission denied page or already redirecting
      const currentPath = window.location.pathname;
      const isAlreadyOnPermissionPage =
        currentPath.includes("permission-denied");
      const isAlreadyRedirecting = sessionStorage.getItem("redirecting_403");

      // Don't redirect if already on permission page or already redirecting
      if (isAlreadyOnPermissionPage || isAlreadyRedirecting === "true") {
        return Promise.reject(error);
      }

      // Set flag to prevent multiple redirects
      sessionStorage.setItem("redirecting_403", "true");

      const message =
        error.response.data?.message ||
        "You do not have permission to perform this action. Please contact your administrator.";

      // Redirect to permission denied page
      window.location.href = `/permission-denied?message=${encodeURIComponent(
        message
      )}`;

      return Promise.reject(error);
    }

    // Handle 401 - Unauthorized
    if (error.response && error.response.status === 401) {
      const refreshToken = localStorage.getItem("tykoon_refresh");
      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/admin/refreshToken`,
            {}, // Pass an empty body if not required
            {
              headers: {
                "Content-type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${JSON.parse(refreshToken)}`,
              },
            }
          );

          // Save new tokens using the `setToken` utility
          setToken(refreshResponse.data);

          // Retry the original request with the new token
          const newAccessToken = refreshResponse.data.accessToken;
          error.config.headers.Authorization = `Bearer ${newAccessToken}`;
          return api.request(error.config);
        } catch (refreshError) {
          console.error("Failed to refresh token", refreshError);

          // Dispatch a logout action if refresh fails
          localStorage.removeItem("tykoon_access");
          localStorage.removeItem("tykoon_refresh");
          window.location.href = "/signin";
          return Promise.reject(refreshError);
        }
      } else {
        localStorage.removeItem("tykoon_access");
        localStorage.removeItem("tykoon_refresh");
        window.location.href = "/signin";
      }
    }

    return Promise.reject(error);
  }
);

export default api;
